<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>تسليم عهدة</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          تسليم عهدة جديدة
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Main table -->
    <v-data-table
      :headers="headers"
      :items="visibleCustodies"
      :search="search"
      :loading="loading"
      :items-per-page="10"
      :sort-by="[{key: 'id', order: 'desc'}]"
      class="elevation-1 rtl-table"
    >
      <template #[`item.type`]="{ item }">
        {{ getTypeText(item.type) }}
      </template>
      <template #[`item.date_from`]="{ item }">
        {{ formatDate(item.date_from) }}
      </template>
      <template #[`item.date_to`]="{ item }">
        {{ formatDate(item.date_to) }}
      </template>
      <template #[`item.amount`]="{ item }">
        {{ formatNumber(item.amount) }} د.ل
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip
          :color="getStatusColor(item.status)"
          text-color="white"
          size="small"
        >
          {{ getStatusText(item.status) }}
        </v-chip>
      </template>
      <template #[`item.created_by`]="{ item }">
        {{ item.created_by_name || 'غير معروف' }}
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon small class="mr-2" @click="viewItem(item)" title="عرض">
          mdi-eye
        </v-icon>
        <v-icon 
          small 
          class="mr-2" 
          @click="editItem(item)" 
          v-if="canEdit(item)"
          title="تعديل"
        >
          mdi-pencil
        </v-icon>
        <v-icon
          small
          class="mr-2"
          @click="printReceipt(item)"
          v-if="isApprovedForPrint(item)"
          color="primary"
          title="طباعة إيصال"
        >
          mdi-printer
        </v-icon>
        <v-icon 
          small 
          class="mr-2" 
          @click="approveItem(item)" 
          v-if="canApprove(item)"
          color="success"
          title="إعتماد"
        >
          mdi-check-circle
        </v-icon>
        <v-icon
          v-if="!isApprovedForPrint(item) && canCancelSettlement(item)"
          small
          class="mr-2"
          color="warning"
          @click="promptCancelSettlement(item)"
          title="إلغاء"
        >
          mdi-cancel
        </v-icon>
      </template>
    </v-data-table>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="showAddDialog" max-width="900px" persistent>
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">
          {{ editedIndex === -1 ? 'تسليم عهدة جديدة' : 'تعديل العهدة' }}
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.type"
                  :items="bookingTypes"
                  item-title="text"
                  item-value="value"
                  label="نوع الحجز"
                  :rules="[v => !!v || 'نوع الحجز مطلوب']"
                  required
                  class="rtl-input"
                  @update:model-value="fetchSummary"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.amount"
                  label="المبلغ المسلم"
                  type="number"
                  :rules="[v => !!v || 'المبلغ مطلوب', v => v > 0 || 'المبلغ يجب أن يكون أكبر من صفر']"
                  required
                  class="rtl-input"
                  suffix="د.ل"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_from"
                  label="من تاريخ"
                  type="date"
                  :rules="[v => !!v || 'التاريخ مطلوب']"
                  required
                  class="rtl-input"
                  @update:model-value="fetchSummary"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.date_to"
                  label="إلى تاريخ"
                  type="date"
                  :rules="[v => !!v || 'التاريخ مطلوب']"
                  required
                  class="rtl-input"
                  @update:model-value="fetchSummary"
                ></v-text-field>
              </v-col>
            </v-row>
            
            <!-- ملخص الفترة من الخادم (مسجّلات الحجوزات في النظام) -->
            <v-card v-if="summaryLoading || summary || summaryError" class="mb-4 pa-4" color="blue-grey-lighten-5" elevation="0">
              <v-card-title class="text-subtitle-1 pa-0 mb-3 d-flex align-center justify-space-between flex-wrap">
                <span>ملخص الفترة المحددة</span>
                <span v-if="summaryLoading" class="text-caption text-medium-emphasis">جاري التحميل…</span>
              </v-card-title>
              <p v-if="summaryError && !summaryLoading" class="text-caption text-error mb-3">{{ summaryError }}</p>
              <v-row v-if="summary && !summaryLoading" dense>
                <v-col cols="6" md="3">
                  <div class="text-center pa-2">
                    <div class="text-h6 font-weight-bold primary--text">{{ summary.total_bookings }}</div>
                    <div class="text-caption">عدد الحجوزات</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center pa-2">
                    <div class="text-h6 font-weight-bold">{{ formatNumber(summary.total_amount) }} د.ل</div>
                    <div class="text-caption">إجمالي المبلغ</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center pa-2">
                    <div class="text-h6 font-weight-bold success--text">{{ formatNumber(summary.total_paid) }} د.ل</div>
                    <div class="text-caption">إجمالي المدفوع</div>
                  </div>
                </v-col>
                <v-col cols="6" md="3">
                  <div class="text-center pa-2">
                    <div class="text-h6 font-weight-bold error--text">{{ formatNumber(summary.total_remaining) }} د.ل</div>
                    <div class="text-caption">المتبقي</div>
                  </div>
                </v-col>
              </v-row>
            </v-card>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.note"
                  label="ملاحظات"
                  rows="3"
                  class="rtl-input"
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="closeDialog">
            إلغاء
          </v-btn>
          <v-btn color="primary" variant="text" @click="save" :disabled="!valid">
            حفظ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View Dialog -->
    <v-dialog v-model="showViewDialog" max-width="800px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">تفاصيل العهدة</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <strong>نوع الحجز:</strong> {{ getTypeText(viewedItem.type) }}
            </v-col>
            <v-col cols="6">
              <strong>الحالة:</strong> 
              <v-chip :color="getStatusColor(viewedItem.status)" text-color="white" size="small">
                {{ getStatusText(viewedItem.status) }}
              </v-chip>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <strong>من تاريخ:</strong> {{ formatDate(viewedItem.date_from) }}
            </v-col>
            <v-col cols="6">
              <strong>إلى تاريخ:</strong> {{ formatDate(viewedItem.date_to) }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <strong>المبلغ المسلم:</strong> {{ formatNumber(viewedItem.amount) }} د.ل
            </v-col>
          </v-row>

          <!-- Summary Section for Admin Verification -->
          <v-card v-if="viewedItem.summary" class="my-4 pa-4" color="blue-grey-lighten-5" elevation="0">
            <v-card-title class="text-subtitle-1 pa-0 mb-3">إحصائيات الفترة المحددة (للتحقق)</v-card-title>
            <v-row dense>
              <v-col cols="6" md="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold primary--text">{{ viewedItem.summary.total_bookings }}</div>
                  <div class="text-caption">عدد الحجوزات</div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold">{{ formatNumber(viewedItem.summary.total_amount) }} د.ل</div>
                  <div class="text-caption">إجمالي المبلغ</div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold success--text">{{ formatNumber(viewedItem.summary.total_paid) }} د.ل</div>
                  <div class="text-caption">إجمالي المدفوع</div>
                </div>
              </v-col>
              <v-col cols="6" md="3">
                <div class="text-center pa-2">
                  <div class="text-h6 font-weight-bold error--text">{{ formatNumber(viewedItem.summary.total_remaining) }} د.ل</div>
                  <div class="text-caption">المتبقي</div>
                </div>
              </v-col>
            </v-row>
          </v-card>

          <v-row v-if="approveNoteDisplay(viewedItem) || isApprovedForPrint(viewedItem)" class="mt-2">
            <v-col cols="12">
              <v-sheet
                v-if="approveNoteDisplay(viewedItem)"
                rounded
                class="pa-3"
                color="green-lighten-5"
                style="border: 1px solid rgba(0, 0, 0, 0.08);"
              >
                <div class="text-caption text-medium-emphasis mb-1">ملاحظات الإعتماد</div>
                <div class="text-body-2">{{ approveNoteDisplay(viewedItem) }}</div>
              </v-sheet>
              <div v-else-if="isApprovedForPrint(viewedItem)" class="text-caption text-medium-emphasis">
                لا توجد ملاحظات إعتماد مسجّلة.
              </div>
            </v-col>
          </v-row>
          <v-row v-if="isSoftDeleted(viewedItem)" class="mt-1">
            <v-col cols="12">
              <strong>تاريخ الإلغاء:</strong> {{ formatDateTime(viewedItem.deleted_at) }}
            </v-col>
          </v-row>
          <v-row v-if="viewedItem.note">
            <v-col cols="12">
              <strong>ملاحظات:</strong> {{ viewedItem.note }}
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <strong>تم الإنشاء بواسطة:</strong> {{ viewedItem.created_by_name || 'غير معروف' }}
            </v-col>
            <v-col cols="6">
              <strong>تاريخ الإنشاء:</strong> {{ formatDateTime(viewedItem.created_at) }}
            </v-col>
          </v-row>
          <v-row v-if="viewedItem.approved_by_name || viewedItem.approved_at">
            <v-col cols="6">
              <strong>تم الإعتماد بواسطة:</strong> {{ viewedItem.approved_by_name || '—' }}
            </v-col>
            <v-col cols="6">
              <strong>تاريخ الإعتماد:</strong> {{ formatDateTime(viewedItem.approved_at) }}
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="canCancelSettlement(viewedItem)"
            color="warning"
            variant="text"
            @click="promptCancelSettlement(viewedItem)"
          >
            <v-icon start>mdi-cancel</v-icon>
            إلغاء السجل
          </v-btn>
          <v-btn
            v-if="canApprove(viewedItem)"
            color="success"
            variant="text"
            @click="approveFromView"
          >
            <v-icon start>mdi-check-circle</v-icon>
            إعتماد
          </v-btn>
          <v-btn
            v-if="isApprovedForPrint(viewedItem)"
            color="primary"
            variant="text"
            @click="printReceipt(viewedItem)"
          >
            <v-icon start>mdi-printer</v-icon>
            طباعة
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="showViewDialog = false">
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- تأكيد إلغاء — السجل يبقى في الجدول بحالة ملغى -->
    <v-dialog v-model="showCancelConfirmDialog" max-width="460px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h6">إلغاء العهدة</v-card-title>
        <v-card-text>
          تأكيد إلغاء هذا السجل؟ سيظل ظاهراً في الجدول بحالة «ملغى» ولا يمكن تعديله أو اعتماده بعد ذلك.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dismissCancelSettlementDialog">تراجع</v-btn>
          <v-btn color="warning" variant="text" @click="confirmCancelSettlement">تأكيد الإلغاء</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Approve Dialog -->
    <v-dialog v-model="showApproveDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-h5">إعتماد العهدة</v-card-title>
        <v-card-text>
          <p>هل أنت متأكد من إعتماد هذه العهدة؟</p>
          <v-textarea
            v-model="approvalNote"
            label="ملاحظات الإعتماد (إختياري)"
            rows="3"
            class="rtl-input mt-3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="text" @click="showApproveDialog = false">
            إلغاء
          </v-btn>
          <v-btn color="success" variant="text" @click="confirmApprove">
            إعتماد
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print Receipt Section (Hidden) -->
    <div id="custodyPrintSection" style="display: none;">
      <div style="font-family: 'Cairo', sans-serif; direction: rtl; max-width: 600px; margin: 0 auto; padding: 20px; color: #000;">
        <div style="text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 12px;">
          <img src="@/assets/soufara.png" alt="Logo" style="max-width: 180px; margin-bottom: 8px;">
          <h2 style="font-size: 16px; margin: 0; font-weight: 600;">مركز سفراء العلم للتدريب</h2>
        </div>
        <div style="text-align: center; margin-bottom: 15px;">
          <h1 style="font-size: 20px; margin: 0; font-weight: bold;">إيصال تسليم عهدة</h1>
          <div style="font-size: 12px; margin-top: 3px;">التاريخ: {{ receiptData.print_date }}</div>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin: 15px 0; font-size: 13px;">
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold; width: 35%;">نوع الحجز</td>
            <td style="padding: 6px 10px; text-align: right;">{{ receiptData.type_text }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">الفترة من</td>
            <td style="padding: 6px 10px; text-align: right;">{{ receiptData.date_from }}</td>
          </tr>
          <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 6px 10px; font-weight: bold;">الفترة إلى</td>
            <td style="padding: 6px 10px; text-align: right;">{{ receiptData.date_to }}</td>
          </tr>
        </table>
        
        <div style="margin: 15px 0; padding: 12px; background-color: #f5f5f5; border-radius: 4px;">
          <div style="font-weight: bold; margin-bottom: 8px; text-align: center; font-size: 13px;">إحصائيات الفترة</div>
          <table style="width: 100%; border-collapse: collapse; font-size: 11px;">
            <tr>
              <td style="padding: 4px 5px; text-align: center; border-left: 1px solid #ddd;">عدد الحجوزات</td>
              <td style="padding: 4px 5px; text-align: center; border-left: 1px solid #ddd;">إجمالي المبلغ</td>
              <td style="padding: 4px 5px; text-align: center; border-left: 1px solid #ddd;">إجمالي المدفوع</td>
              <td style="padding: 4px 5px; text-align: center;">المتبقي</td>
            </tr>
            <tr>
              <td style="padding: 6px 5px; text-align: center; font-weight: bold; border-left: 1px solid #ddd;">{{ receiptData.total_bookings }}</td>
              <td style="padding: 6px 5px; text-align: center; font-weight: bold; border-left: 1px solid #ddd;">{{ receiptData.total_amount }} د.ل</td>
              <td style="padding: 6px 5px; text-align: center; font-weight: bold; color: #4caf50; border-left: 1px solid #ddd;">{{ receiptData.total_paid }} د.ل</td>
              <td style="padding: 6px 5px; text-align: center; font-weight: bold; color: #f44336;">{{ receiptData.total_remaining }} د.ل</td>
            </tr>
          </table>
        </div>

        <div style="margin: 15px 0; padding: 12px; border: 2px solid #000;">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <tr>
              <td style="padding: 5px 0; font-weight: bold;">المبلغ المسلم</td>
              <td style="padding: 5px 0; text-align: left; font-weight: bold; font-size: 16px;">{{ receiptData.amount }} د.ل</td>
            </tr>
          </table>
        </div>

        <div v-if="receiptData.note" style="margin: 15px 0; padding: 10px; background-color: #fff3cd; border-radius: 4px;">
          <div style="font-weight: bold; margin-bottom: 5px;">ملاحظات:</div>
          <div style="font-size: 12px;">{{ receiptData.note }}</div>
        </div>

        <div style="font-size: 12px; margin: 12px 0; padding: 10px 0; border-top: 1px solid #ddd;">
          <div style="margin-bottom: 5px;">
            <span style="font-weight: bold;">تم التسليم بواسطة:</span>
            <span style="margin-right: 8px;">{{ receiptData.created_by_name }}</span>
          </div>
          <div style="margin-bottom: 5px;">
            <span style="font-weight: bold;">تاريخ التسليم:</span>
            <span style="margin-right: 8px;">{{ receiptData.created_at }}</span>
          </div>
          <div style="margin-bottom: 5px;">
            <span style="font-weight: bold;">تم الإعتماد بواسطة:</span>
            <span style="margin-right: 8px;">{{ receiptData.approved_by_name }}</span>
          </div>
          <div>
            <span style="font-weight: bold;">تاريخ الإعتماد:</span>
            <span style="margin-right: 8px;">{{ receiptData.approved_at }}</span>
          </div>
        </div>

        <div v-if="receiptData.approval_note" style="margin: 15px 0; padding: 10px; background-color: #e8f5e9; border-radius: 4px;">
          <div style="font-weight: bold; margin-bottom: 5px;">ملاحظات الإعتماد:</div>
          <div style="font-size: 12px;">{{ receiptData.approval_note }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import custodyHandoverService from '@/services/custodyHandoverService';
import { fetchAndSummarizeCustodyPeriod } from '@/utils/custodyPeriodStats';

const UI_TO_API_BOOKING = {
  room: 'room_booking',
  space: 'space_booking',
  course: 'course_booking'
};

const API_TO_UI_BOOKING = {
  room_booking: 'room',
  space_booking: 'space',
  course_booking: 'course',
  course_reservation: 'course',
  room: 'room',
  space: 'space',
  course: 'course'
};

export default {
  name: 'CustodyHandover',
  data() {
    return {
      search: '',
      loading: false,
      showAddDialog: false,
      showViewDialog: false,
      showApproveDialog: false,
      showCancelConfirmDialog: false,
      itemToCancelSettlement: null,
      valid: false,
      custodyHandovers: [],
      editedIndex: -1,
      editedItem: {
        type: '',
        date_from: '',
        date_to: '',
        amount: 0,
        note: ''
      },
      defaultItem: {
        type: '',
        date_from: '',
        date_to: '',
        amount: 0,
        note: ''
      },
      viewedItem: {},
      itemToApprove: null,
      approvalNote: '',
      summary: null,
      summaryLoading: false,
      summaryError: '',
      receiptData: {
        type_text: '',
        date_from: '',
        date_to: '',
        total_bookings: 0,
        total_amount: 0,
        total_paid: 0,
        total_remaining: 0,
        amount: 0,
        note: '',
        created_by_name: '',
        created_at: '',
        approved_by_name: '',
        approved_at: '',
        approval_note: '',
        print_date: ''
      },
      headers: [
        { title: 'نوع الحجز', key: 'type', align: 'center' },
        { title: 'من تاريخ', key: 'date_from', align: 'center' },
        { title: 'إلى تاريخ', key: 'date_to', align: 'center' },
        { title: 'المبلغ', key: 'amount', align: 'center' },
        { title: 'الحالة', key: 'status', align: 'center' },
        { title: 'تم الإنشاء بواسطة', key: 'created_by', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ],
      bookingTypes: [
        { text: 'حجز قاعات', value: 'room' },
        { text: 'حجز مساحات دراسة', value: 'space' },
        { text: 'حجز دورات', value: 'course' }
      ]
    };
  },
  computed: {
    ...mapGetters('auth', ['hasAnyRole']),
    currentUserId() {
      const user = this.$store.state.auth?.user;
      return user?.id != null ? Number(user.id) : null;
    },
    isAdmin() {
      return this.hasAnyRole(['admin', 'super_admin']);
    },
    /** استقبال: إضافة/تعديل عهداته المعلّقة فقط + عرض ما قدّمه */
    isReception() {
      return this.hasAnyRole(['Reception', 'reception']);
    },
    visibleCustodies() {
      if (this.isAdmin) return this.custodyHandovers;
      if (this.isReception) {
        return this.custodyHandovers.filter(i => this.isMine(i));
      }
      return this.custodyHandovers.filter(i => this.isMine(i));
    }
  },
  methods: {
    extractListFromResponse(res) {
      const body = res?.data;
      if (body == null) return [];
      const inner = body.data !== undefined ? body.data : body;
      if (Array.isArray(inner)) return inner;
      if (inner?.data && Array.isArray(inner.data)) return inner.data;
      return [];
    },

    extractRecordFromResponse(res) {
      const body = res?.data;
      if (body == null) return null;
      let payload = body.data !== undefined ? body.data : body;
      while (
        payload &&
        typeof payload === 'object' &&
        !Array.isArray(payload) &&
        payload.data !== undefined &&
        typeof payload.data === 'object' &&
        payload.data !== null &&
        !Array.isArray(payload.data)
      ) {
        payload = payload.data;
      }
      return payload;
    },

    bookingTypeToUi(apiType) {
      if (apiType == null || apiType === '') return '';
      const k = String(apiType)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/-/g, '_');
      return API_TO_UI_BOOKING[k] || API_TO_UI_BOOKING[String(apiType).trim()] || k;
    },

    bookingTypeToApi(uiType) {
      return UI_TO_API_BOOKING[uiType] || uiType;
    },

    /** قيمة soft-delete من الحقول العربية أو camelCase Laravel */
    parseDeletedAtValue(v) {
      if (v == null) return null;
      const s = String(v).trim();
      if (!s || s.toLowerCase() === 'null') return null;
      return s;
    },

    normalizeSettlement(raw) {
      if (!raw) return null;
      let createdBy = raw.created_by ?? raw.createdBy;
      if (createdBy && typeof createdBy === 'object') {
        createdBy = createdBy.id;
      }
      if (createdBy == null && raw.creator?.id != null) {
        createdBy = raw.creator.id;
      }
      const uiType = this.bookingTypeToUi(
        raw.booking_type ?? raw.bookingType ?? raw.type ?? raw.booking_kind
      );
      const deletedSrc = raw.deleted_at ?? raw.deletedAt ?? null;
      const deletedAt = this.parseDeletedAtValue(deletedSrc);

      let status = String(raw.status ?? 'pending').trim().toLowerCase().replace(/\s+/g, '_');

      const cancelStatuses = ['cancelled', 'canceled', 'void'];
      if (cancelStatuses.includes(status)) status = 'cancelled';
      if (deletedAt) status = 'cancelled';

      const entered =
        raw.entered_amount != null
          ? raw.entered_amount
          : raw.enteredAmount != null
            ? raw.enteredAmount
            : raw.amount;
      const summary =
        raw.summary ||
        raw.period_summary ||
        raw.period_statistics ||
        raw.stats ||
        null;

      const creatorName =
        raw.created_by_name ||
        raw.createdByName ||
        raw.creator?.name ||
        raw.creator?.username ||
        (typeof raw.created_by === 'object' ? raw.created_by?.name || raw.created_by?.username : '') ||
        (typeof raw.createdBy === 'object' ? raw.createdBy?.name || raw.createdBy?.username : '') ||
        raw.user?.name ||
        '';

      return {
        id: raw.id,
        type: uiType,
        booking_type:
          raw.booking_type ?? raw.bookingType ?? (uiType ? this.bookingTypeToApi(uiType) : ''),
        date_from: raw.date_from ?? raw.dateFrom ?? '',
        date_to: raw.date_to ?? raw.dateTo ?? '',
        amount: Number(entered) || 0,
        note: raw.note || '',
        status,
        deleted_at: deletedAt,
        created_by: createdBy != null ? Number(createdBy) : null,
        created_by_name: creatorName,
        created_at: raw.created_at ?? raw.createdAt,
        approved_by: raw.approved_by ?? raw.approvedBy,
        approved_by_name:
          raw.approved_by_name ||
          raw.approvedByName ||
          raw.approver?.name ||
          raw.approver?.username ||
          '',
        approved_at: raw.approved_at ?? raw.approvedAt,
        approval_note: raw.approval_note || raw.approve_note || raw.approveNote || '',
        summary
      };
    },

    buildApiPayload(form) {
      return {
        booking_type: this.bookingTypeToApi(form.type),
        date_from: form.date_from,
        date_to: form.date_to,
        entered_amount: Number(form.amount),
        note: form.note || ''
      };
    },

    isMine(item) {
      if (this.currentUserId == null || item?.created_by == null) return false;
      return Number(item.created_by) === Number(this.currentUserId);
    },

    isSoftDeleted(item) {
      if (!item) return false;
      if (String(item.status || '').toLowerCase() === 'cancelled') return true;
      return this.parseDeletedAtValue(item.deleted_at) != null;
    },

    isPending(item) {
      if (this.isSoftDeleted(item)) return false;
      return String(item?.status || '').toLowerCase() === 'pending';
    },

    isApprovedForPrint(item) {
      if (this.isSoftDeleted(item)) return false;
      const s = String(item?.status || '').toLowerCase();
      return s === 'approved' || s === 'completed';
    },

    async fetchCustodyHandovers(params = {}) {
      this.loading = true;
      try {
        const res = await custodyHandoverService.list({ per_page: 500, ...params });
        const rows = this.extractListFromResponse(res);
        this.custodyHandovers = rows.map(r => this.normalizeSettlement(r)).filter(Boolean);
      } catch (e) {
        console.error(e);
        this.custodyHandovers = [];
        this.$store.dispatch('showAlert', {
          message: e.response?.data?.message || 'تعذر تحميل سجلات تسليم العهدة',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },

    /** نفس أرقام الإيصال — من أي كائن summary يعتمده الخادم في السجل أو في مجيب الملخص */
    periodTotals(summaryLike) {
      if (!summaryLike || typeof summaryLike !== 'object') {
        return {
          total_bookings: 0,
          total_amount: 0,
          total_paid: 0,
          total_remaining: 0
        };
      }
      const n = x => (x == null || x === '' ? 0 : Number(x));
      return {
        total_bookings: n(summaryLike.total_bookings ?? summaryLike.bookings_count ?? summaryLike.count),
        total_amount: n(summaryLike.total_amount ?? summaryLike.amount_total),
        total_paid: n(summaryLike.total_paid ?? summaryLike.paid_total),
        total_remaining: n(
          summaryLike.total_remaining ?? summaryLike.remaining ?? summaryLike.remaining_amount
        )
      };
    },

    /**
     * مطابقة الإيصال: إن وُجدت أرقام summary على السجل نفس ما يطبع الإيصال.
     * وإلا (إضافة، أو نغير النوع/الفترة) نجمع الحجوزات من واجهات القاعات/المساحات/الدورات بنفس صافي المبلغ والمدفوع/المتبقي كشاشات الحجز.
     */
    async fetchSummary() {
      this.summaryError = '';
      const { type, date_from, date_to } = this.editedItem;
      if (!type || !date_from || !date_to) {
        this.summary = null;
        this.summaryLoading = false;
        return;
      }
      if (new Date(date_from) > new Date(date_to)) {
        this.summary = null;
        this.summaryError = 'تاريخ البداية يجب أن يكون قبل أو يساوي تاريخ النهاية';
        this.summaryLoading = false;
        return;
      }

      const row = this.editedIndex >= 0 ? this.custodyHandovers[this.editedIndex] : null;
      const sameAsListedRow =
        row &&
        row.type === type &&
        row.date_from === date_from &&
        row.date_to === date_to;
      if (sameAsListedRow) {
        this.summary = this.periodTotals(row.summary);
        this.summaryLoading = false;
        return;
      }

      const reqId = (this._summaryReqId = (this._summaryReqId || 0) + 1);
      this.summaryLoading = true;
      this.summary = null;
      try {
        const totals = await fetchAndSummarizeCustodyPeriod(type, date_from, date_to);
        if (reqId !== this._summaryReqId) return;
        this.summary = this.periodTotals(totals);
      } catch (e) {
        console.error(e);
        if (reqId !== this._summaryReqId) return;
        this.summary = null;
        this.summaryError =
          e.response?.data?.message || e.message || 'تعذر حساب ملخص الفترة من الحجوزات';
      } finally {
        if (reqId === this._summaryReqId) this.summaryLoading = false;
      }
    },

    async viewItem(item) {
      this.loading = true;
      try {
        const res = await custodyHandoverService.getById(item.id);
        const rec = this.extractRecordFromResponse(res);
        let normalized = this.normalizeSettlement(rec) || { ...item };
        if (!normalized.summary && normalized.type && normalized.date_from && normalized.date_to) {
          try {
            const totals = await fetchAndSummarizeCustodyPeriod(
              normalized.type,
              normalized.date_from,
              normalized.date_to
            );
            normalized = {
              ...normalized,
              summary: this.periodTotals(totals)
            };
          } catch (e) {
            console.warn('period summary for view', e);
          }
        }
        this.viewedItem = normalized;
        this.showViewDialog = true;
      } catch (e) {
        console.error(e);
        this.viewedItem = { ...item };
        this.showViewDialog = true;
        this.$store.dispatch('showAlert', {
          message: e.response?.data?.message || 'تعذر تحميل التفاصيل',
          type: 'warning'
        });
      } finally {
        this.loading = false;
      }
    },

    editItem(item) {
      if (this.isSoftDeleted(item)) return;
      this.editedIndex = this.custodyHandovers.findIndex(i => i.id === item.id);
      if (this.editedIndex < 0) return;
      const src = this.custodyHandovers[this.editedIndex];
      this.editedItem = {
        type: src.type,
        date_from: src.date_from,
        date_to: src.date_to,
        amount: src.amount,
        note: src.note || ''
      };
      this.showAddDialog = true;
      this.fetchSummary();
    },

    closeDialog() {
      this.showAddDialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.editedIndex = -1;
        this.summary = null;
        this.summaryError = '';
        this.summaryLoading = false;
      });
    },

    async save() {
      const { valid } = await this.$refs.form.validate();
      if (!valid) return;

      try {
        const payload = this.buildApiPayload(this.editedItem);
        if (this.editedIndex > -1) {
          const id = this.custodyHandovers[this.editedIndex].id;
          const res = await custodyHandoverService.update(id, payload);
          const rec = this.extractRecordFromResponse(res);
          if (rec) {
            this.custodyHandovers.splice(this.editedIndex, 1, this.normalizeSettlement(rec));
          } else {
            await this.fetchCustodyHandovers();
          }
          this.$store.dispatch('showAlert', {
            message: res?.data?.message || 'تم تحديث العهدة بنجاح',
            type: 'success'
          });
        } else {
          const res = await custodyHandoverService.create(payload);
          const rec = this.extractRecordFromResponse(res);
          if (rec) {
            this.custodyHandovers.unshift(this.normalizeSettlement(rec));
          } else {
            await this.fetchCustodyHandovers();
          }
          this.$store.dispatch('showAlert', {
            message: res?.data?.message || 'تم إضافة العهدة بنجاح',
            type: 'success'
          });
        }
        this.closeDialog();
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', {
          message: e.response?.data?.message || 'حدث خطأ أثناء الحفظ',
          type: 'error'
        });
      }
    },

    approveItem(item) {
      if (this.isSoftDeleted(item)) return;
      this.itemToApprove = item;
      this.approvalNote = '';
      this.showApproveDialog = true;
    },

    approveFromView() {
      this.itemToApprove = { ...this.viewedItem };
      this.showViewDialog = false;
      this.approvalNote = '';
      this.showApproveDialog = true;
    },

    openPrintWindow() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        this.$store.dispatch('showAlert', {
          message: 'تعذر فتح نافذة الطباعة. يرجى السماح بالنوافذ المنبثقة ثم المحاولة مرة أخرى.',
          type: 'warning'
        });
        return null;
      }
      printWindow.document.write(`
        <!DOCTYPE html>
        <html dir="rtl">
          <head>
            <meta charset="utf-8">
            <title>إيصال تسليم عهدة</title>
            <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">
            <style>
              * { font-family: 'Cairo', sans-serif !important; }
              body { margin: 0; padding: 20px; text-align: center; color: #555; }
            </style>
          </head>
          <body>جاري تحضير الإيصال...</body>
        </html>
      `);
      printWindow.document.close();
      return printWindow;
    },

    writeCustodyPrintWindow(printWindow, printContent) {
      printWindow.document.open();
      printWindow.document.write(`
        <!DOCTYPE html>
        <html dir="rtl">
          <head>
            <meta charset="utf-8">
            <title>إيصال تسليم عهدة</title>
            <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&display=swap" rel="stylesheet">
            <style>
              * { font-family: 'Cairo', sans-serif !important; }
              body { margin: 0; padding: 20px; }
              @media print { body { margin: 0; padding: 10px; } }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = function () {
        setTimeout(function () {
          printWindow.focus();
          printWindow.print();
          printWindow.onafterprint = function () {
            printWindow.close();
          };
        }, 500);
      };
    },

    /**
     * إحصائيات الإيصال: نفس تجميع الحجوزات في نافذة الإضافة/التعديل؛ عند تعذر الجلب نُبقي summary المسجّل بالسجل إن وُجد.
     */
    async printReceipt(item) {
      if (this.isSoftDeleted(item)) {
        this.$store.dispatch('showAlert', {
          message: 'لا يمكن طباعة إيصال لسجل أُلغي',
          type: 'warning'
        });
        return;
      }

      const printWindow = this.openPrintWindow();
      if (!printWindow) return;

      let summary = this.periodTotals(item?.summary);
      if (item.type && item.date_from && item.date_to) {
        try {
          const totals = await fetchAndSummarizeCustodyPeriod(
            item.type,
            item.date_from,
            item.date_to
          );
          summary = this.periodTotals(totals);
        } catch (e) {
          console.warn('custody print stats', e);
        }
      }

      this.receiptData = {
        type_text: this.getTypeText(item.type),
        date_from: this.formatDate(item.date_from),
        date_to: this.formatDate(item.date_to),
        total_bookings: summary.total_bookings,
        total_amount: this.formatNumber(summary.total_amount),
        total_paid: this.formatNumber(summary.total_paid),
        total_remaining: this.formatNumber(summary.total_remaining),
        amount: this.formatNumber(item.amount),
        note: item.note || '',
        created_by_name: item.created_by_name || 'غير معروف',
        created_at: this.formatDateTime(item.created_at),
        approved_by_name: item.approved_by_name || 'غير معروف',
        approved_at: this.formatDateTime(item.approved_at),
        approval_note: item.approval_note || item.approve_note || '',
        print_date: this.formatDateTime(new Date().toISOString())
      };

      await this.$nextTick();
      const printContent = document.getElementById('custodyPrintSection')?.innerHTML;
      if (!printContent) {
        printWindow.close();
        this.$store.dispatch('showAlert', {
          message: 'تعذر تحضير محتوى الإيصال للطباعة',
          type: 'error'
        });
        return;
      }

      this.writeCustodyPrintWindow(printWindow, printContent);
    },

    async confirmApprove() {
      if (!this.itemToApprove?.id) return;
      try {
        const body = { approve_note: (this.approvalNote || '').trim() };
        const res = await custodyHandoverService.approve(this.itemToApprove.id, body);
        const rec = this.extractRecordFromResponse(res);
        if (rec) {
          const norm = this.normalizeSettlement(rec);
          const ix = this.custodyHandovers.findIndex(i => i.id === norm.id);
          if (ix > -1) this.custodyHandovers.splice(ix, 1, norm);
        } else {
          await this.fetchCustodyHandovers();
        }
        this.$store.dispatch('showAlert', {
          message: res?.data?.message || 'تم إعتماد العهدة بنجاح',
          type: 'success'
        });
        this.showApproveDialog = false;
        this.itemToApprove = null;
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', {
          message: e.response?.data?.message || 'حدث خطأ أثناء الإعتماد',
          type: 'error'
        });
      }
    },

    dismissCancelSettlementDialog() {
      this.showCancelConfirmDialog = false;
      this.itemToCancelSettlement = null;
    },

    /** إلغاء السجل: مشرفون فقط (الاستقبال لا يرى زر الإلغاء ولا يستطيعونه) */
    canCancelSettlement(item) {
      if (!item?.id || this.isSoftDeleted(item)) return false;
      return this.isAdmin && this.isPending(item);
    },

    reconcileCanceledSettlementRow(prevSnap, srvRow, delNorm) {
      const merged = { ...prevSnap, ...srvRow };

      const delTs =
        this.parseDeletedAtValue(delNorm?.deleted_at) ||
        this.parseDeletedAtValue(srvRow.deleted_at) ||
        this.parseDeletedAtValue(merged.deleted_at);

      if (!merged.type && prevSnap.type) merged.type = prevSnap.type;
      if (!merged.booking_type && prevSnap.booking_type) merged.booking_type = prevSnap.booking_type;
      if (!merged.date_from && prevSnap.date_from) merged.date_from = prevSnap.date_from;
      if (!merged.date_to && prevSnap.date_to) merged.date_to = prevSnap.date_to;

      const prevAmt = Number(prevSnap.amount) || 0;
      const srvAmt = Number(srvRow.amount) || 0;
      if (!(srvAmt > 0) && prevAmt > 0) merged.amount = prevAmt;

      if (!merged.created_by_name && prevSnap.created_by_name) merged.created_by_name = prevSnap.created_by_name;
      if (merged.created_by == null && prevSnap.created_by != null) merged.created_by = prevSnap.created_by;

      const delSt = delNorm?.status ? String(delNorm.status).toLowerCase() : '';
      const srvSt = String(srvRow.status || '').toLowerCase();
      if (delTs) {
        merged.deleted_at = delTs;
        merged.status = 'cancelled';
      } else if (delSt === 'cancelled' || srvSt === 'cancelled') {
        merged.status = 'cancelled';
      }
      return merged;
    },

    finalizeRowAfterSuccessfulCancel(row) {
      if (!row) return row;
      return {
        ...row,
        status: 'cancelled',
        deleted_at: this.parseDeletedAtValue(row.deleted_at) || new Date().toISOString(),
      };
    },

    promptCancelSettlement(item) {
      if (!item?.id || this.isSoftDeleted(item) || !this.isAdmin) return;
      this.itemToCancelSettlement = { ...item };
      this.showCancelConfirmDialog = true;
    },

    async confirmCancelSettlement() {
      if (!this.itemToCancelSettlement?.id) return;
      const prevSnap = { ...this.itemToCancelSettlement };
      const id = prevSnap.id;
      try {
        const res = await custodyHandoverService.remove(id);
        const delRaw = this.extractRecordFromResponse(res);
        const delNorm = delRaw ? this.normalizeSettlement(delRaw) : null;

        await this.fetchCustodyHandovers({ _fresh: Date.now() });

        const ix = this.custodyHandovers.findIndex(i => Number(i.id) === Number(id));
        let updated =
          ix > -1 ? this.reconcileCanceledSettlementRow(prevSnap, this.custodyHandovers[ix], delNorm) : null;
        updated = updated ? this.finalizeRowAfterSuccessfulCancel(updated) : null;

        if (ix > -1 && updated) {
          this.custodyHandovers.splice(ix, 1, updated);
        }

        if (
          updated &&
          this.showViewDialog &&
          this.viewedItem?.id != null &&
          Number(this.viewedItem.id) === Number(id)
        ) {
          this.viewedItem = { ...updated };
        }
        this.$store.dispatch('showAlert', {
          message:
            res?.data?.message || 'تم إلغاء العهدة؛ السجل يبقى في الجدول بحالة ملغى',
          type: 'success'
        });
      } catch (e) {
        console.error(e);
        this.$store.dispatch('showAlert', {
          message: e.response?.data?.message || 'تعذر تنفيذ الإلغاء',
          type: 'error'
        });
      } finally {
        this.showCancelConfirmDialog = false;
        this.itemToCancelSettlement = null;
      }
    },

    approveNoteDisplay(item) {
      const n = item?.approval_note ?? item?.approve_note ?? '';
      const t = String(n).trim();
      return t || '';
    },

    canEdit(item) {
      if (this.isSoftDeleted(item)) return false;
      if (!this.isPending(item)) return false;
      if (!this.isMine(item)) return false;
      return true;
    },

    canApprove(item) {
      if (this.isSoftDeleted(item)) return false;
      return this.isAdmin && this.isPending(item);
    },

    getTypeText(type) {
      const typeObj = this.bookingTypes.find(t => t.value === type);
      return typeObj ? typeObj.text : type;
    },

    getStatusColor(status) {
      const s = String(status || '').toLowerCase();
      switch (s) {
        case 'approved':
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'rejected':
          return 'error';
        case 'cancelled':
          return 'grey';
        default:
          return 'grey';
      }
    },

    getStatusText(status) {
      const s = String(status || '').toLowerCase();
      switch (s) {
        case 'approved':
        case 'completed':
          return 'معتمد';
        case 'pending':
          return 'قيد الانتظار';
        case 'rejected':
          return 'مرفوض';
        case 'cancelled':
          return 'ملغى';
        default:
          return status || '';
      }
    },

    formatNumber(number) {
      return Number(number).toLocaleString('ar-LY');
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString('ar-LY');
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleString('ar-LY', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  },

  created() {
    this.fetchCustodyHandovers();
  }
};
</script>

<style scoped>
.rtl-table {
  direction: rtl;
}

.rtl-dialog {
  direction: rtl;
  text-align: right;
}

.rtl-input :deep(.v-field__input) {
  direction: rtl;
  text-align: right;
}
</style>
